"use client";

import { usePathname } from "next/navigation";
import { forwardRef } from "react";
import type { ComponentPropsWithoutRef } from "react";

import { PrefetchLink } from "@/components/prefetch-link.client";
import { resetScrollOnNavigate } from "@/lib/reset-scroll-on-navigate";

import styles from "./nav-menu.module.css";

/**
 * Blurring ends `:focus-within`, but the pointer still sits on the panel, so
 * `:hover` kept it open over the next page (TC-65 #5). The bar item is marked
 * dismissed, and the CSS hides its panels, until the visitor next reaches for
 * it: the pointer leaving, a press, or focus coming back. Leave alone is not
 * enough — a keyboard Enter never moves the pointer, and a touch tap fires its
 * leave before the click — so either would hide that menu for good.
 */
function dismissPanel(link: HTMLElement): void {
  const item = link.closest<HTMLElement>(`.${styles.barItem}`);
  if (!item) return;
  item.dataset.dismissed = "";
  const done = new AbortController();
  const restore = (): void => {
    delete item.dataset.dismissed;
    done.abort();
  };
  for (const type of ["pointerleave", "pointerdown", "focusin"] as const) {
    item.addEventListener(type, restore, { signal: done.signal });
  }
}

/**
 * A link inside a `NavMenuBar` panel. The panel is opened by CSS alone —
 * `group-focus-within` in `nav-menu.tsx` — and a click leaves the anchor
 * focused. A full page load would reset that; a client-side navigation keeps
 * the header mounted, so the still-focused link held the panel open over the
 * next page. Blurring on click is what ends `:focus-within`, and it runs after
 * any handler the caller merged in (`SheetClose asChild` on mobile).
 *
 * `aria-current="page"` marks the item for the route the visitor is on; the
 * bar's classes in `nav-menu.tsx` draw it in the agency's colour.
 *
 * A plain path opts out of Next's post-navigation scroll and resets to the
 * top itself (see `resetScrollOnNavigate`); a `/path#section` keeps Next's
 * scroll so it lands on its section. That lives here, not in `nav-menu.tsx`,
 * because `NavMenuBar` also renders from Server Components (the header
 * builder's Nav Menu), which cannot pass an `onClick` to a client part.
 */
export const NavMenuLink = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<typeof PrefetchLink>
>(function NavMenuLink({ onClick, ...props }, ref) {
  const pathname = usePathname();
  const hasAnchor = String(props.href).includes("#");
  return (
    <PrefetchLink
      aria-current={props.href === pathname ? "page" : undefined}
      scroll={hasAnchor ? undefined : false}
      {...props}
      ref={ref}
      onClick={(event) => {
        onClick?.(event);
        if (!hasAnchor) resetScrollOnNavigate();
        dismissPanel(event.currentTarget);
        event.currentTarget.blur();
      }}
    />
  );
});
